import React, { useState } from 'react';
import moment from 'moment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { ExportDatabase } from 'api/ExportHubAPI';
import { getDatabaseIcon, DEFAULT_ACCENT } from './databasePresets';

interface Props {
  database: ExportDatabase;
  onOpen: (db: ExportDatabase) => void;
  onDelete: (db: ExportDatabase) => void;
}

const DatabaseCard: React.FC<Props> = ({ database, onOpen, onDelete }) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const Icon = getDatabaseIcon(database.icon);
  const accent = database.accent_color || DEFAULT_ACCENT;
  const health = Math.max(
    0,
    Math.min(100, Math.round(database.compliance_health || 0)),
  );

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = (): void => {
    setMenuAnchor(null);
  };
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setMenuAnchor(null);
    onDelete(database);
  };

  return (
    <div
      className='eh-db-card'
      role='button'
      tabIndex={0}
      onClick={() => onOpen(database)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(database);
        }
      }}
    >
      <div className='eh-db-card-head'>
        <span
          className='eh-db-card-icon'
          style={{ color: accent, background: `${accent}1f` }}
        >
          <Icon style={{ fontSize: 22 }} />
        </span>
        <div className='eh-db-card-head-right'>
          {database.visibility === 'Private' && (
            <span className='eh-db-card-visibility' title='Private database'>
              <LockOutlinedIcon style={{ fontSize: 13 }} /> Private
            </span>
          )}
          <IconButton
            size='small'
            className='eh-db-card-menu-btn'
            aria-label={`Actions for ${database.name}`}
            onClick={openMenu}
          >
            <MoreVertIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={handleDelete}
          style={{ color: 'var(--low-color)', gap: 8 }}
        >
          <DeleteOutlineIcon style={{ fontSize: 18 }} /> Delete database
        </MenuItem>
      </Menu>

      <div className='eh-db-card-name'>{database.name}</div>
      <div className='eh-db-card-desc'>
        {database.description || 'No description'}
      </div>

      <div className='eh-db-card-kpis'>
        <div className='eh-db-kpi'>
          <span className='eh-db-kpi-value'>{database.product_count}</span>
          <span className='eh-db-kpi-label'>Products</span>
        </div>
        <div className='eh-db-kpi'>
          <span className='eh-db-kpi-value'>
            {database.recent_screening_count}
          </span>
          <span className='eh-db-kpi-label'>Screenings</span>
        </div>
        <div className='eh-db-kpi eh-db-kpi-health'>
          <span className='eh-db-kpi-value'>{health}%</span>
          <span className='eh-db-kpi-label'>Health</span>
          <div className='eh-db-health-track'>
            <div
              className='eh-db-health-fill'
              style={{ width: `${health}%` }}
            />
          </div>
        </div>
      </div>

      <div className='eh-db-card-foot'>
        Updated {moment(database.last_activity_at).fromNow()}
      </div>
    </div>
  );
};

export default DatabaseCard;
